import { BarChart3 } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { TurnTrackerState } from '../../redux/types/charterWsTypes'

interface TurnIndicatorProps {
  turnTracker: TurnTrackerState
  memberNames: Record<string, string>
  currentUserId?: string
}

export function TurnIndicator({ turnTracker, memberNames, currentUserId }: TurnIndicatorProps) {
  const entries = Object.entries(turnTracker)
  if (entries.length === 0) return null

  const maxWordCount = Math.max(...entries.map(([, t]) => t.word_count), 1)
  const totalWords = entries.reduce((s, [, t]) => s + t.word_count, 0)

  return (
    <Card className='shadow-none border-gray-200'>
      <CardHeader className='pb-2 pt-3 px-3'>
        <CardTitle className='text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5'>
          <BarChart3 className='h-3.5 w-3.5' />
          Participation
        </CardTitle>
      </CardHeader>
      <CardContent className='px-3 pb-3 space-y-2.5'>
        {entries.map(([memberId, tracker]) => {
          const name = memberNames[memberId] ?? `User ${memberId}`
          const pct = totalWords > 0 ? Math.round((tracker.word_count / totalWords) * 100) : 0
          const isCurrentUser = memberId === String(currentUserId)
          const isOveractive = tracker.consecutive_messages >= 3
          const isSilent = tracker.message_count === 0

          return (
            <div key={memberId} className='space-y-1'>
              <div className='flex justify-between items-center'>
                <span
                  className={cn(
                    'text-xs font-medium truncate max-w-[120px]',
                    isCurrentUser ? 'text-indigo-700' : 'text-gray-700',
                    isSilent && 'text-gray-400 italic'
                  )}
                >
                  {name}
                  {isCurrentUser && ' (you)'}
                </span>
                <div className='flex items-center gap-1'>
                  {isOveractive && (
                    <span className='text-xs text-amber-600 font-medium'>↑ Lots</span>
                  )}
                  {isSilent && (
                    <span className='text-xs text-gray-400 italic'>Silent</span>
                  )}
                  <span className='text-xs text-gray-400 tabular-nums'>{pct}%</span>
                </div>
              </div>
              <Progress
                value={(tracker.word_count / maxWordCount) * 100}
                className={cn(
                  'h-1.5',
                  isOveractive ? '[&>div]:bg-amber-400' : '[&>div]:bg-indigo-400',
                  isSilent && 'opacity-30'
                )}
              />
              <p className='text-xs text-gray-400'>
                {tracker.message_count} msg{tracker.message_count !== 1 ? 's' : ''} ·{' '}
                {tracker.word_count} words
              </p>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
